<template>
  <TitleLayout class="payment_new_card">
    <template #header-left>
      <Back @click="back"></Back>
    </template>

    <template #default>
      <SolidPaymentMethod
        v-if="self.splitPrimer.isInited && self.splitGeoPayment.isInited"
        :pkg="pkg"
        :is-authorize-card-token="isSubscription"
        :is-apple-pay-enabled="self.isApplePayEnabled"
        :is-google-pay-enabled="self.isGooglePayEnabled"
        :is-card-enabled="!self.splitPrimer.isV2"
        :google-pay-btn-type="'buy'"
      >
        <template
          #default="{ solidCardElId, solidGooglePayElId, solidApplePayElId, solidPayPalElId }"
        >
          <div class="title-h1">{{ t('choose') }}</div>

          <div class="flex mb-8">
            <div
              class="bg-success-500 w-1/2 p-3 mr-2 flex flex-col items-center justify-center rounded-xl border border-success"
            >
              <img src="./images/paypal.svg?url" alt="Paypal" />
            </div>
            <div
              class="w-1/2 p-3 ml-2 flex flex-col items-center rounded-xl border border-dark-400 cursor-pointer"
              @click="self.setIsCardFormVisible(true)"
            >
              <div class="mb-1 text-button2 font-semibold">{{ t('creditCard') }}</div>
              <div v-if="self.isAppleGooglePayIconsVisible" class="flex gap-2">
                <span class="text-button2 font-semibold text-dark-200">{{ t('or') }}</span>
                <img v-if="self.isApplePayIconVisible" src="./images/apple-pay.svg?url" alt="" />
                <img v-if="self.isGooglePayIconVisible" src="./images/google-pay.svg?url" alt="" />
              </div>

              <div v-else class="flex gap-2">
                <img src="./images/credit-card.svg?url" alt="Credit card" />
              </div>
            </div>
          </div>

          <template v-if="areMethodsLoaded">
            <div class="payment-express">
              <div v-if="!isSubscription">
                <div class="payment_btn_wrap">
                  <div class="payment_btn_wrap-label">{{ t('expressCheckout') }}</div>
                  <div
                    :id="solidPayPalElId"
                    class="h-[40px] overflow-hidden flex justify-center"
                  ></div>
                </div>
              </div>
            </div>

            <SolidPaymentMethodModalLayout
              v-show="self.isCardFormVisible"
              :pkg="pkg"
              payment-flow="default"
              :is-authorize-card-token="isSubscription"
              @close="!self.isPaypalAvailable ? back() : self.setIsCardFormVisible(false)"
            >
              <template #title>{{ t('paymentDetails') }}</template>

              <template #default>
                <div class="relative">
                  <div :id="solidGooglePayElId" class="mb-2"></div>
                  <div :id="solidApplePayElId" class="mb-2"></div>

                  <div :id="solidCardElId" class="solid-payment-form-container"></div>
                  <PrimerPaymentMethod
                    v-if="self.splitPrimer.isV2"
                    :pkg="pkg"
                  ></PrimerPaymentMethod>

                  <template v-if="!isTermsChecked && !self.isFromLanding">
                    <div
                      class="absolute bg-black opacity-50 left-0 top-0 right-0 bottom-0 rounded"
                    ></div>

                    <div
                      class="absolute left-0 top-0 right-0 bottom-0 flex items-center justify-center text-white"
                    >
                      {{ t('terms') }}
                    </div>
                  </template>
                </div>

                <div v-if="!self.isFromLanding" class="custom-checkbox accept_form is-invalid mt-5">
                  <TermsCheckboxInput v-model="isTermsChecked"></TermsCheckboxInput>
                </div>
              </template>
            </SolidPaymentMethodModalLayout>
          </template>
          <div v-else>
            <SpinnerLoader />
          </div>
        </template>
      </SolidPaymentMethod>
    </template>
  </TitleLayout>
</template>

<script lang="ts" setup>
import { onMounted, PropType, ref } from 'vue';

import TermsCheckboxInput from '@/components/inputs/checkbox/TermsCheckboxInput.vue';
import Back from '@/components/layout/components/header/components/back/Back.vue';
import TitleLayout from '@/components/layout/TitleLayout.vue';
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue';
import PrimerPaymentMethod from '@/components/payment/components/payment-method/components/primer/primer-payment-method/PrimerPaymentMethod.vue';
import SolidPaymentMethod from '@/components/payment/components/payment-method/components/solid/solid-payment-method/SolidPaymentMethod.vue';
import SolidPaymentMethodModalLayout from '@/components/payment/components/payment-method/components/solid/solid-payment-method-modal-layout/SolidPaymentMethodModalLayout.vue';
import { usePayment } from '@/components/payment/hooks/use-payment/use-payment';
import { trackPaymentFlow } from '@/helpers/track/track-payment/track-payment';
import { useHistoryChange } from '@/hooks/use-history-change/use-history-change';
import { useSelf } from '@/pages/payment/components/payment-method/components/payment-method-add-paypal-first/hooks/use-self/use-self';
import { PaymentStatePackage } from '@/pages/payment/store/payment-state';
import { useI18n } from '@/services/i18n';

import { messages } from './i18n';

const props = defineProps({
  pkg: {
    type: Object as PropType<PaymentStatePackage>,
    required: true,
  },
  isGooglePayVisible: {
    type: Boolean,
    default: true,
  },
  isApplePayVisible: {
    type: Boolean,
    default: true,
  },
  isSubscription: {
    type: Boolean,
    default: false,
  },
  isCardFormForced: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back']);

const { t } = useI18n({ messages });

const areMethodsLoaded = ref(false);

const isTermsChecked = ref(false);

const self = useSelf({
  pkg: props.pkg,
  isApplePayVisible: props.isApplePayVisible,
  isGooglePayVisible: props.isGooglePayVisible,
});

// Hook ------------------------------------------------------------------------

function back() {
  emit('back');
}

onMounted(async () => {
  trackPaymentFlow({
    step: 'add',
  });
});

usePayment({
  loaded: () => {
    areMethodsLoaded.value = true;

    if (!self.isPaypalAvailable || props.isCardFormForced) {
      self.setIsCardFormVisible(true);
    }
  },
});

self.init();

useHistoryChange(back).init();
</script>

<style lang="scss" src="./style.scss"></style>
